import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { Chart, Declarant } from '@e-origin/shared';

import { useAdminCreateChart, useAdminEditChart } from '@admin/hooks';

const chartSchema = z.object({
  name: z.string().min(1, { message: 'Chart name is required' }),
  order: z.number(),
  forSupervisorOnly: z.boolean().optional(),
  isChart: z.boolean().optional(),
  declarants: z.array(z.string()).optional(),
  chartId: z.string().min(1, { message: 'Chart ID is required' }),
});

export const useEditChartFormInitializer = ({ chart }) => {
  const createChartMutation = useAdminCreateChart();
  const editChartMutation = useAdminEditChart();

  const onSubmit = async (values) => {
    if (values._id) {
      return await editChartMutation.mutateAsync({
        params: { id: values._id },
        body: values,
      });
    } else {
      return await createChartMutation.mutateAsync({ body: values });
    }
  };

  const buildInitialValues = (chart: Chart) => ({
    _id: chart?._id,
    name: chart?.name || '',
    order: chart?.order || 1,
    forSupervisorOnly: chart?.forSupervisorOnly || false,
    isChart: chart?.isChart ?? false,
    declarants: Array.isArray(chart?.declarants) ? chart.declarants.map((d) => (d as unknown as Declarant)._id) : [],
    chartId: chart?.chartId || '',
  });

  const form = useForm({
    initialValues: buildInitialValues(chart),
    validate: zodResolver(chartSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(chart));
  }, [chart]);

  return {
    form,
    onSubmit,
    isSubmitting: createChartMutation.isPending || editChartMutation.isPending,
  };
};
